import type { Deal, DealStage } from '@/types'
import { checkTransition } from '@/services/dealTransition.service'

export type StageQualificationPayload = {
  budgetConfirmado?: string
  decisorPresente?: string
  doresIdentificadas?: string
}

export type StageTransitionIntent =
  | { type: 'noop' }
  | { type: 'blocked'; reason: string; code?: string; highlightFields?: string[] }
  | { type: 'open_stage_choice'; dealId: string; targetStage: DealStage }
  | { type: 'open_loss_modal'; dealId: string; targetStage: DealStage }
  | { type: 'open_future_modal'; dealId: string; targetStage: DealStage }
  | { type: 'open_win_modal'; dealId: string; targetStage: DealStage }
  | { type: 'open_bant_modal'; dealId: string; targetStage: DealStage }
  | {
      type: 'patch'
      dealId: string
      targetStage: DealStage
      qualification?: StageQualificationPayload
    }

export function getBantQualificationFromDeal(
  deal: Pick<Deal, 'budgetConfirmado' | 'decisorPresente' | 'doresIdentificadas'>,
): StageQualificationPayload {
  return {
    budgetConfirmado: deal.budgetConfirmado,
    decisorPresente: deal.decisorPresente,
    doresIdentificadas: deal.doresIdentificadas,
  }
}

/** Decide o próximo passo ao mover deal de etapa (Kanban, painel, etc.). */
export function resolveStageTransitionIntent(
  deal: Deal,
  targetStage: DealStage,
  userRole?: string | null,
): StageTransitionIntent {
  if (deal.etapa === targetStage) return { type: 'noop' }

  if (targetStage === 'perdido') {
    return { type: 'open_loss_modal', dealId: deal.id, targetStage }
  }

  if (targetStage === 'fechamento_futuro') {
    return { type: 'open_future_modal', dealId: deal.id, targetStage }
  }

  if (targetStage === 'ganho') {
    const check = checkTransition(deal, targetStage, userRole)
    if (!check.allowed) {
      return {
        type: 'blocked',
        reason: check.reason ?? 'Só é possível marcar como ganho após o handover.',
        code: check.code,
      }
    }
    return { type: 'open_win_modal', dealId: deal.id, targetStage }
  }

  // Etapas explícitas após proposta enviada — patch direto (modal já escolheu o destino).
  const propostaEnviadaDirect: DealStage[] = [
    'gerar_contrato',
    'teste_gratis',
    'renegociacao',
    'perdido',
    'pausado',
    'fechamento_futuro',
  ]
  if (deal.etapa === 'proposta_enviada' && !propostaEnviadaDirect.includes(targetStage)) {
    return { type: 'open_stage_choice', dealId: deal.id, targetStage }
  }

  const check = checkTransition(deal, targetStage, userRole)
  if (!check.allowed) {
    if (check.code === 'REQUIRED_FIELD_MISSING' && targetStage === 'proposta_elaboracao') {
      return { type: 'open_bant_modal', dealId: deal.id, targetStage }
    }
    return {
      type: 'blocked',
      reason: check.reason ?? 'Transição não permitida',
      code: check.code,
      highlightFields:
        check.code === 'REUNIAO_REQUIRED'
          ? (check.missingFields ?? ['dataReuniao'])
          : undefined,
    }
  }

  const qualification =
    deal.etapa === 'reuniao_realizada' && targetStage === 'proposta_elaboracao'
      ? getBantQualificationFromDeal(deal)
      : undefined

  return { type: 'patch', dealId: deal.id, targetStage, qualification }
}
